import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { AddPublicationToFavoritesPayloadI, FavoritesFileI, FavoritesFolderI } from 'src/app/models/favorites';
import { PublicationTypeE } from 'src/app/models/bookPartLink';
import { AuthenticateService } from 'src/app/services/authenticate.service';
import { FavoritesRepository, HeaderMenuInformationRepository } from 'src/app/store';
import { PdfDownloadTypeI } from 'src/app/online/folder/service/folder.service';

@Injectable({ providedIn: 'root' })
export class FavoritesService {
    private serviceUrl = 'webfront/favorites';

    constructor(
        private httpClient: HttpClient,
        private authenticateService: AuthenticateService,
        private favoritesRepository: FavoritesRepository,
        private headerMenuInformationRepository: HeaderMenuInformationRepository
    ) {}

    get currentUserUuid(): string {
        return this.authenticateService.currentUser.uuid;
    }

    getFolders(): Observable<FavoritesFolderI[]> {
        return this.httpClient.get<FavoritesFolderI[]>(`${this.serviceUrl}/folders`);
    }

    createFolder(name: string): Observable<FavoritesFolderI> {
        return this.httpClient.post<FavoritesFolderI>(`${this.serviceUrl}/folders`, { name });
    }

    renameFolder(id: number, name: string): Observable<FavoritesFolderI> {
        return this.httpClient.put<FavoritesFolderI>(`${this.serviceUrl}/folders/${id}`, { name });
    }

    deleteFolder(id: number, withFavorites = false): Observable<unknown> {
        return this.httpClient
            .delete(`${this.serviceUrl}/folders/${id}`, { params: { withFavorites } })
            .pipe(tap(() => this.fetchUserFavorites()));
    }

    downloadFolderPdf(folder: FavoritesFolderI): Observable<PdfDownloadTypeI> {
        return this.httpClient
            .get(`${this.serviceUrl}/folders/${folder.id}/pdf`, { responseType: 'blob' })
            .pipe(map(file => ({ file, filename: `${folder.name}.pdf` })));
    }

    fetchFolderPublications(favoritesFolderId: number): Observable<FavoritesFileI[]> {
        return this.httpClient.get<FavoritesFileI[]>(this.serviceUrl, {
            params: { favoritesFolderId: `${favoritesFolderId}` }
        });
    }

    fetchUnfiledPublications(): Observable<FavoritesFileI[]> {
        return this.httpClient.get<FavoritesFileI[]>(this.serviceUrl);
    }

    fetchAllPublicationsHydrated(): Observable<FavoritesFileI[]> {
        return this.httpClient.get<FavoritesFileI[]>(`${this.serviceUrl}/all-hydrated`);
    }

    fetchUserFavorites(): void {
        this.httpClient.get<FavoritesFileI[]>(`${this.serviceUrl}/all`).subscribe(favorites => {
            this.favoritesRepository.setFavorites(this.currentUserUuid, favorites);
            this.headerMenuInformationRepository.updateInformation({ numberDocument: favorites.length });
        });
    }

    selectFavorites$(): Observable<FavoritesFileI[]> {
        return this.favoritesRepository.selectFavorites$(this.currentUserUuid);
    }

    selectFavoriteByPublication$(
        publicationId: number,
        publicationType: PublicationTypeE
    ): Observable<FavoritesFileI | undefined> {
        return this.favoritesRepository.selectFavoriteByPublication$(
            this.currentUserUuid,
            publicationId,
            publicationType
        );
    }

    addPublicationToFavorites(payload: AddPublicationToFavoritesPayloadI): Observable<FavoritesFileI> {
        return this.httpClient.post<FavoritesFileI>(this.serviceUrl, payload).pipe(
            tap(favorite => {
                this.favoritesRepository.addFavorite(this.currentUserUuid, favorite);
                this.headerMenuInformationRepository.incrementInformationNumberDocument();
            })
        );
    }

    moveFavorite(favoritesFileId: number, favoritesFolderId?: number | null): Observable<FavoritesFileI> {
        return this.httpClient
            .put<FavoritesFileI>(`${this.serviceUrl}/${favoritesFileId}/move`, { favoritesFolderId })
            .pipe(tap(favorite => this.favoritesRepository.updateFavorite(this.currentUserUuid, favorite)));
    }

    removeFavorite(favoritesFileId: number): Observable<unknown> {
        return this.httpClient.delete(`${this.serviceUrl}/${favoritesFileId}`).pipe(
            tap(() => {
                this.favoritesRepository.removeFavorite(this.currentUserUuid, favoritesFileId);
                this.headerMenuInformationRepository.decrementInformationNumberDocumentBy(1);
            })
        );
    }
}
